import React from 'react'
import DocumentIcon from '@tecuity/sos-icons/react/document'
import ActionButton, { ActionButtonGroup } from '../ActionButton/ActionButton'
import { Spinner } from '../Loaders/Loaders'
import { PageError } from '../PageError/PageError'

const formatCellValue = (value, dataType) => {
  if (value == null) return '—'
  if (dataType === 'Date' || dataType === 'DateTime') return new Date(value).toLocaleDateString()
  if (value === 'true') return 'Yes'
  if (value === 'false') return 'No'
  return value
}

/**
 * Reusable results table for Miscellaneous record searches (public search and My Records).
 *
 * Props:
 *   columns       – array of { Key, Label, DataType } from the template
 *   items         – array of result rows; each row has RecordNum, RegistrationDate, Fields, RecordId
 *   totalCount    – total matching records (may exceed items.length when capped at 100)
 *   loading       – boolean
 *   error         – truthy when an error occurred
 *   selectedRecordId – RecordId of the currently open drawer row (or null)
 *   onRowClick    – (row) => void called when the first column cell is clicked
 *   onViewFiling  – optional (row) => void; when provided, renders an Actions column with a View button
 */
const MiscellaneousRecordTable = ({
  columns,
  items,
  totalCount,
  loading,
  error,
  selectedRecordId,
  onRowClick,
  onViewFiling,
}) => {
  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <PageError />
  }

  if (!items || !items.length) {
    return null
  }

  const getCellValue = (row, col) => {
    if (col.Key === 'RecordNum') return row.RecordNum
    if (col.Key === 'RegistrationDate') return row.RegistrationDate
    return (row.Fields && row.Fields[col.Key]) || null
  }

  return (
    <div className="table-wrapper">
      <div className="row-count" role="status" aria-live="assertive" aria-atomic="true">
        <div className="row-count-text">
          {totalCount > items.length
            ? 'Showing ' + items.length + ' of ' + totalCount + ' results'
            : totalCount + ' result' + (totalCount === 1 ? '' : 's')}
        </div>
      </div>
      <table className="div-table center-container" role="table">
        <thead className="div-table-heading">
          <tr role="row">
            {(columns || []).map((col) => (
              <th key={col.Key} className="div-table-head" role="columnheader">
                <div className="flex-row">
                  <span className="header-label">{col.Label}</span>
                </div>
              </th>
            ))}
            {onViewFiling && (
              <th className="div-table-head" role="columnheader">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="div-table-body">
          {items.map((row) => {
            const isSelected = row.RecordId === selectedRecordId
            return (
              <tr
                key={row.RecordId}
                className={'div-table-row' + (isSelected ? ' selected' : '')}
                role="row"
              >
                {(columns || []).map((col, colIndex) => {
                  const isFirst = colIndex === 0
                  const cellValue = getCellValue(row, col)
                  const formatted = formatCellValue(cellValue, col.DataType)
                  return (
                    <td
                      key={col.Key}
                      className={'div-table-cell' + (isFirst ? ' interactive' : '') + (isFirst && isSelected ? ' selected' : '')}
                      role="gridcell"
                    >
                      {isFirst ? (
                        <>
                          <div
                            role="button"
                            tabIndex={0}
                            className="interactive-cell-button"
                            onClick={() => { onRowClick && onRowClick(row) }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                onRowClick && onRowClick(row)
                              }
                            }}
                          >
                            <span className="cell">{formatted}</span>
                            <span className="icon icon-caret-right" />
                          </div>
                        </>
                      ) : (
                        <span className="cell">{formatted}</span>
                      )}
                    </td>
                  )
                })}
                {onViewFiling && (
                  <td className="div-table-cell no-padding action-cell" role="gridcell">
                    <ActionButtonGroup buttonSize="extra-small" className="table-button">
                      <ActionButton
                        label="View Filing"
                        className="primary"
                        hideLabel={true}
                        tooltip={true}
                        onClick={() => { onViewFiling(row) }}
                      >
                        <DocumentIcon />
                      </ActionButton>
                    </ActionButtonGroup>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default MiscellaneousRecordTable
